import * as survey from "@aidkitorg/types/lib/survey";
import React, { Suspense, useContext, useRef, useState } from "react";
import { usePost } from "./API";
import Papa from "papaparse";
import { ConfigurationContext, PublicConfigurationContext } from "./Context";
import { BUTTON_CLASS, AidKitLogo } from "./Util";
import { Link } from 'react-router-dom';

const Distro = React.lazy(() => import("@aidkitorg/typesheets/lib/distroeditor"));

function utf8_to_b64(str: string) {
    return window.btoa(unescape(encodeURIComponent(str)));
}

function b64_to_utf8(str: string) {
    return decodeURIComponent(escape(window.atob(str)));
}

function tryDecodingHash(hash: string) {
    try {
        return JSON.parse(b64_to_utf8(hash));
    } catch (e) {
        return null;
    }
} 

export function ImportPage() {
    const configContext = useContext(ConfigurationContext);
    const publicConfig = useContext(PublicConfigurationContext);
  
    const importCSV = usePost('/program/admin/import_csv');
    const createImportSignature = usePost('/program/admin/create_import_signature');
    const [data, setData] = useState<Record<string, string>[] | null>(null);
    const [config, setConfig] = useState<survey.CSVMapping>(tryDecodingHash(window.location.hash.slice(1)) || {
        mode: {
            kind: 'Update',
            uidColumn: ''
        },
        columns: [] as survey.CSVMapping['columns']
    });

    const [allowedFields, setAllowedFields] = useState('');
    const [generatedImportUrl, setGeneratedImportUrl] = useState('');

    let distroRef = useRef<React.ComponentRef<typeof Distro>>(null);
    let refsLoaded = useRef(false);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.[0]) {
            Papa.parse<any>(e.target.files?.[0], {
                header: true,
                skipEmptyLines: true,
                complete: function(results: any): undefined {
                    if (results.errors.length > 1) {
                        alert(JSON.stringify(results.errors));
                        return;
                    }
                    setData(results.data);
                },
            });
        }
    };

    async function doImport() {
        const count = data?.length || 0;
        if(
            window.confirm(`This will ${config.mode.kind.toLowerCase()} ${count} record(s) in the ${programName} program. The following columns are being imported:\n\n${config.columns.map(c => c.csvColumn).join('\n')} \n\nAre you sure you wish to proceed?`)
        ){
            const urlParams = new URLSearchParams(window.location.search);
            alert(JSON.stringify(await importCSV({
                data: data!,
                mapping: config,
                import_signature: urlParams.get('import_signature') || '',
            })))    
        }
    }

    const applicantFacingLogo = publicConfig.interface?.applicantFacingLogo?.url || configContext.applicant_facing_logo;
    const applicantFacingLogoWidth = publicConfig.interface?.applicantFacingLogo?.width || configContext.applicant_facing_logo_width || '50';  
    const programName = publicConfig.name || configContext.program_name;

    return <div className="bg-gray-100 p-4">
        {/* banner to indicate which program */}
        <div className="import-banner -m-6 mb-4 px-4 py-2 bg-gray-200 flex gap-4 items-center border-gray-300 border-solid border-b border-l-0 border-t-0 border-r-0">
            <Link to={"/"}>
                {
                applicantFacingLogo ? (
                    <img
                        src={applicantFacingLogo}
                        width={applicantFacingLogoWidth}
                        style={{maxHeight: 30}}
                        alt={programName}
                    />
                    ) : (
                    <AidKitLogo width={60} height={30} />
                    )
                }
            </Link>
            <h2 className="text-sm m-0">{programName}</h2>
        </div>


        <h2>Import</h2>
        <h3>Choose File</h3>
        <div>
            <label
                htmlFor="file-upload"
            >
                <span className={BUTTON_CLASS}>Upload a CSV file</span>
                <input onChange={handleFileUpload} id="file-upload" name="file-upload" type="file" className="sr-only" />
            </label>
        </div>
        {data &&
            <>
                <h3>Columns (so you can copy paste)</h3>
                <ul>
                    {Object.keys(data[0]).map((k) => <li key={k}>{k}</li>)}
                </ul>
                <div className="mt-4 bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                    <Suspense>
                        <Distro 
                            ref={distroRef}
                            value={config}
                            types='src/survey.ts' name='CSVMapping' onChange={(v) => { 
                                window.location.hash = '#' + utf8_to_b64(JSON.stringify(v));
                                setConfig(v);
                                if (!refsLoaded.current && data) {
                                    refsLoaded.current = true;
                                    distroRef.current?.initialize(config, undefined, undefined, {
                                        'csvColumns': Object.keys(data[0])
                                    });
                                }
                            }}/>
                    </Suspense>
                </div>
                <button
                    onClick={doImport}
                    type="button"
                    className={BUTTON_CLASS}
                >
                Import <strong>&nbsp;{data?.length || 0}&nbsp;</strong> record(s) into {programName} 
                </button>
            </>}
        {(configContext.roles || []).includes('admin') &&
            <div>
                <hr className="mt-8"/>
                <h3>Give additional import privileges</h3>
                <div>Use this tool to create an import link for non-admins to use. Using this link, 
                    users with the 'can_import' role will be able to use this page to import values from CSVs, limitied
                    only to the provided allowed fields as columns.
                    The allowed fields should be added as comma separated values.
                </div>
                <div className="mt-4 mb-4 inline-flex">
                    <span className="font-semibold mr-2">Allowed Fields:</span>
                    <textarea 
                        style={{ width: 500 }} 
                        placeholder= 'phone_number, email, ...' 
                        onChange={e => setAllowedFields(e.target.value)}></textarea>
                </div>
                <div>
                    <button 
                        type="button" 
                        className={BUTTON_CLASS}
                        onClick={async () => {
                            const import_signature = await createImportSignature({
                                allowedFields
                            });
                            const url = new URL(window.location.href);
                            url.searchParams.set('import_signature', import_signature);
                            setGeneratedImportUrl(url.href);
                        }}
                    >
                        Generate link
                    </button>
                </div>
                {generatedImportUrl && 
                    <div className="mt-4 mr-4">
                        <span className="font-semibold">Import URL: </span> 
                        <span style={{ overflowWrap: 'break-word' }}>{generatedImportUrl}</span>
                    </div>}
            </div>
        }
    </div>
}
